import { useQuarantineSettings, useUpdateQuarantineSettings } from "@/api/quarantine-settings";
import { useTikTokAdsConnectionStatus } from "@/api/tiktok-ads";
import { AuthoriseTikTokAdsButton } from "@/components/safety-config/authorise-tiktok-ads-button";
import { QuarantineToggleRow } from "@/components/safety-config/QuarantineToggleRow";
import { usePermissions } from "@/hooks/use-permissions";
import { SAFETY_ADMIN_REQUIRED, TIKTOK_ADS_AUTH_REQUIRED } from "@/pages/safety-beta/permission-copy";

/**
 * Quarantine policy toggles.
 *
 * Displayed above the Response policy list in the Safety Policies tab.
 * Each toggle maps to a column in `quarantine_settings` and is persisted
 * via `PUT /merchant/:id/quarantine-settings`.
 *
 * When a toggle is turned OFF the backend soft-deletes the matching
 * `quarantined_videos` rows so the reconcile worker can re-add them to
 * GMV Max on the next tick.
 */
export const QuarantineSection = () => {
  const { data: settings, isLoading } = useQuarantineSettings();
  const { canConfigureSafety } = usePermissions();
  const update = useUpdateQuarantineSettings();
  const { data: adsStatus } = useTikTokAdsConnectionStatus();

  if (isLoading) return null;

  // The settings endpoint returns `null` when the merchant has no
  // merchant_settings row yet (e.g. legacy merchants). Fall back to defaults so
  // the toggles still render and quarantine can be enabled for the first time —
  // the PUT upserts the row.
  const quarantineUnreviewedVideos = settings?.quarantineUnreviewedVideos ?? false;
  const quarantineUnanalysableVideos = settings?.quarantineUnanalysableVideos ?? false;

  // Quarantine is safety config, so it follows canConfigureSafety (UNB-2129).
  const isReadOnly = !canConfigureSafety;
  // Both toggles hold videos back from GMV Max ingestion, so neither works without TikTok Ads authorised.
  const adsBlocked = !adsStatus?.connected;
  const disabledReason = adsBlocked ? TIKTOK_ADS_AUTH_REQUIRED : isReadOnly ? SAFETY_ADMIN_REQUIRED : undefined;

  const handleUnreviewed = (value: boolean) => {
    update.mutate({ quarantineUnreviewedVideos: value });
  };

  const handleUnanalysable = (value: boolean) => {
    update.mutate({ quarantineUnanalysableVideos: value });
  };

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-base font-medium text-secondary">Quarantine</h2>
      <div className="rounded-md border border-border-primary bg-white overflow-hidden">
        <QuarantineToggleRow
          label="Prevent videos with detected risks from being ingested into GMV Max"
          description="Videos with any unresolved risk wait in Needs review until cleared."
          isSelected={!adsBlocked && quarantineUnreviewedVideos}
          isDisabled={adsBlocked || isReadOnly || update.isPending}
          disabledReason={disabledReason}
          action={adsBlocked ? <AuthoriseTikTokAdsButton /> : undefined}
          onChange={handleUnreviewed}
        />

        <QuarantineToggleRow
          label="Prevent videos Unbox cannot analyse from being ingested into GMV Max"
          description="Videos that fail analysis wait in Unable to analyse until reviewed."
          isSelected={!adsBlocked && quarantineUnanalysableVideos}
          isDisabled={adsBlocked || isReadOnly || update.isPending}
          disabledReason={disabledReason}
          action={adsBlocked ? <AuthoriseTikTokAdsButton /> : undefined}
          onChange={handleUnanalysable}
        />
      </div>
    </div>
  );
};
